// ManageNews.js (updated with fixed heights and mobile styling)
import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Container, Card, Spinner, Alert, Badge } from 'react-bootstrap';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import './ManageNews.css';
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';
import { FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';

function ManageNews() {
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch news data from backend
  const fetchNewsData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BaseUrl}/api/news/all`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setNewsData(data);
    } catch (err) {
      console.error("Error fetching news data:", err);
      Swal.fire({
        title: 'Error!',
        text: 'Error fetching news articles: ' + err.message,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsData();
  }, []);

  // Format date function
  const formatDate = dateString => {
    if (!dateString) return 'Not set';
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const handleCreateNews = () => {
    navigate('/createnews');
  };

  const handleEditNews = (newsId) => {
    navigate(`/createnews/${newsId}`);
  };

  const handleDeleteNews = async (newsId, newsTitle) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${newsTitle}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${BaseUrl}/api/news/${newsId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          Swal.fire({
            title: 'Deleted!',
            text: 'News article has been deleted successfully.',
            icon: 'success',
            confirmButtonColor: '#3085d6',
          });
          // Refresh the news list
          fetchNewsData();
        } else {
          throw new Error('Failed to delete news article');
        }
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text: 'Error deleting news article: ' + error.message,
          icon: 'error',
          confirmButtonColor: '#d33',
        });
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      published: { variant: 'success', text: 'Published' },
      draft: { variant: 'warning', text: 'Draft' },
      archived: { variant: 'secondary', text: 'Archived' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  return (
    <>
      <AdminNavbar />
      <Container className="manage-news">
        {/* Header with Create News Button - Fixed for mobile */}
        <div className="page-header-wrapper">
          <h2 className="page-title">Manage News Articles</h2>
          <Button 
            variant="primary" 
            onClick={handleCreateNews}
            className="create-news-btn"
          >
            Create News
          </Button>
        </div>

        {/* News Cards Section */}
        <div className="news-cards-section">
          <h3 className="section-title">All News Articles</h3>
          
          {loading ? (
            <div className="loading-spinner">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <Row>
              {newsData.length > 0 ? (
                newsData.map(newsItem => (
                  <Col md={4} sm={12} className="mb-4" key={newsItem.id}>
                    <Card className="news-card fixed-height-card">
                      <div className="card-image-container">
                        <Card.Img
                          variant="top"
                          src={newsItem.image_url || "/placeholder-image.jpg"}
                          className="fixed-card-img"
                          alt={newsItem.title}
                          onError={e => (e.target.src = "/placeholder-image.jpg")}
                        />
                      </div>
                      <Card.Body className="card-body-content">
                        <div className="card-meta-info">
                          <span>
                            <i className="bi bi-calendar-event"></i> {formatDate(newsItem.publish_date)}
                          </span>
                          <span>
                            <i className="bi bi-clock"></i> {newsItem.read_time || "5 min read"}
                          </span>
                        </div>
                        <Card.Title className="card-title">
                          {newsItem.title}
                        </Card.Title>
                        <Card.Text className="card-excerpt">
                          {newsItem.excerpt}
                        </Card.Text>
                        <div className="card-footer">
                          <div className="badges-container">
                            {getStatusBadge(newsItem.status)}
                            {newsItem.category && (
                              <Badge bg="info" className="category-badge">
                                {newsItem.category}
                              </Badge>
                            )}
                          </div>
                          <div className="action-buttons">
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              className=" edit-btn"
                              onClick={() => handleEditNews(newsItem.id)}
                              title="Edit News"
                            >
                              <FaEdit />
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              className=" delete-btn"
                              onClick={() => handleDeleteNews(newsItem.id, newsItem.title)}
                              title="Delete News"
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              ) : (
                <Col md={12}>
                  <Alert variant="info" className="no-articles-alert">
                    No news articles found. Click "Create News" to create your first article.
                  </Alert>
                </Col>
              )}
            </Row>
          )}
        </div>
      </Container>
    </>
  );
}

export default ManageNews;